// Run-Cache-Layer: holt die DH-Run-Liste vom Backend, parsed + simplifiziert
// die zugehörigen GPXs, speichert in IndexedDB. Spiegelt trails.ts 1:1.

import { getDb } from './storage/db';
import { listRuns, fetchRunGpx } from './api';
import { parseGpx, simplifyTrack, trackLength, elevationStats } from '@adtu/shared';
import type { Difficulty, GpxTrack } from '@adtu/shared';

/**
 * Custom-Event-Name für Komponenten außerhalb der /runs-Seite (z.B. TabBar-
 * Badge), die sich über Run-List-Updates informieren lassen wollen. Wird nach
 * jedem syncRunCache() gefeuert, analog zu TRAILS_UPDATED_EVENT.
 */
export const RUNS_UPDATED_EVENT = 'adt:runs-updated';

export async function getCachedRunCount(): Promise<number> {
  const db = await getDb();
  return db.count('runs');
}

export interface CachedRun {
  id: string;
  name: string;
  difficulty: Difficulty;
  trailIds: string[];
  description?: string | null;
  gpxFilename: string;
  track: GpxTrack;
  stats: { lengthMeters: number; gain: number; loss: number };
  cachedAt: string;
  /** Wave 14dt: optionale Zuordnung zu einer Shuttle-Area (PL/KA/IS/ME/BN
   *  oder Admin-defined). 2-Char-Uppercase, matched zu ShuttleArea.code.
   *  Null/undefined wenn der Admin den Run keiner Area zugewiesen hat —
   *  Frontend rendert ihn dann im Shuttle-Mode nicht beim Area-Tap. */
  shuttleAreaCode?: string | null;
}

const VALID_DIFF = new Set([
  'uphill',
  'easy',
  'intermediate',
  'intermediate_plus',
  'hard',
  'expert',
  'extreme',
  'unknown',
] as const);
function normalizeDifficulty(v?: string | null): Difficulty {
  if (v && VALID_DIFF.has(v as Difficulty)) return v as Difficulty;
  return 'unknown';
}

/**
 * Lädt die Run-Liste vom Backend und synchronisiert den IDB-Cache. Gibt alle
 * aktuell gecachten Runs zurück (auch wenn das Backend offline ist).
 *
 * Parallelitäts-Hinweis: Wenn zwei Tabs gleichzeitig die App benutzen, könnten
 * beide syncen. Das ist gutartig — idb transaktioniert pro put(), die Rows
 * werden idempotent überschrieben. Keine explizite Mutex nötig.
 *
 * Dispatch-Verhalten (Wave 14bv hotfix): `RUNS_UPDATED_EVENT` wird NUR
 * gefeuert wenn der Cache sich tatsächlich verändert hat (neue IDs,
 * geänderte updatedAt-Stamps oder Entfernungen). Vorher wurde der Event
 * immer gedispatched — in Kombination mit MapPage, das auf den Event mit
 * erneutem `syncRunCache` reagiert, gab es einen Infinite-Loop.
 */
export async function syncRunCache(opts: { domParser: DOMParser }): Promise<CachedRun[]> {
  const db = await getDb();

  // Snapshot des alten Cache-Zustands, damit wir am Ende vergleichen können
  // ob sich was geändert hat. Keys (id, cachedAt) reichen als Fingerprint —
  // wenn sich die IDs oder cachedAt-Werte ändern, ist was passiert.
  const beforeRows = await db.getAll('runs');
  const beforeFingerprint = beforeRows
    .map((r) => `${r.id}@${r.cachedAt}`)
    .sort()
    .join('|');

  let remote: Awaited<ReturnType<typeof listRuns>> | null = null;
  try {
    remote = await listRuns();
  } catch {
    // offline — weiter mit lokalem Cache
  }

  if (remote) {
    for (const r of remote.runs) {
      const existing = await db.get('runs', r.id);
      const metaChanged = existing && (
        existing.name !== r.name ||
        existing.difficulty !== normalizeDifficulty(r.difficulty) ||
        existing.description !== (r.description ?? null) ||
        JSON.stringify(existing.trailIds) !== JSON.stringify(r.trailIds) ||
        // Wave 14dt-fix2: shuttleAreaCode-Wechsel als Cache-Invalidator.
        // Defense-in-depth zum buildRunDto-MAX(mtime)-Fix im Backend — wenn
        // aus irgendeinem Grund die mtime nicht bumpt (z.B. nach Cron-Sync
        // der nur über rsync den content kopiert), greift dieser Check.
        ((existing.shuttleAreaCode ?? null) !== (r.shuttleAreaCode ?? null))
      );
      if (existing && existing.cachedAt >= r.updatedAt && !metaChanged) continue;

      try {
        const xml = await fetchRunGpx(r.gpxFilename);
        const parsedTracks = parseGpx(xml, { domParser: opts.domParser });
        if (parsedTracks.length === 0) continue;
        const track = parsedTracks[0]!;
        const simplified: GpxTrack = { name: track.name, points: simplifyTrack(track.points, 4) };
        await db.put('runs', {
          id: r.id,
          name: r.name,
          difficulty: normalizeDifficulty(r.difficulty),
          trailIds: r.trailIds,
          description: r.description ?? null,
          gpxFilename: r.gpxFilename,
          gpxRaw: xml,
          track: simplified,
          cachedAt: r.updatedAt,
          // Wave 14dt: Shuttle-Area-Zuordnung mitcachen (kann null sein
          // wenn der Run keiner Area zugewiesen ist).
          shuttleAreaCode: r.shuttleAreaCode ?? null,
        });
      } catch (err) {
        console.warn('Failed to cache run', r.gpxFilename, err);
      }
    }

    // Wave 14bz — Deletes durchreichen (analog trails.ts).
    // Server-seitig gelöschte Runs aus dem lokalen IDB entfernen, damit der
    // User sie nicht weiter auf der Karte sieht. Nur ausführen wenn remote
    // SUCCESS — offline-Start lässt den Cache sonst nicht wegfliegen.
    const remoteIds = new Set(remote.runs.map((r) => r.id));
    const localRuns = await db.getAll('runs');
    for (const local of localRuns) {
      if (!remoteIds.has(local.id)) {
        try {
          await db.delete('runs', local.id);
        } catch (err) {
          console.warn('[runs] stale delete failed', local.id, err);
        }
      }
    }
  }

  const all = await db.getAll('runs');
  // Dispatch Update-Event NUR wenn der Cache-Fingerprint sich geändert hat
  // (Wave 14bv hotfix — verhindert den MapPage-syncRunCache-Loop).
  const afterFingerprint = all
    .map((r) => `${r.id}@${r.cachedAt}`)
    .sort()
    .join('|');
  if (afterFingerprint !== beforeFingerprint) {
    try {
      window.dispatchEvent(new CustomEvent(RUNS_UPDATED_EVENT));
    } catch {
      /* SSR-Pfad — egal */
    }
  }
  return all.map((r) => ({
    id: r.id,
    name: r.name,
    difficulty: normalizeDifficulty(r.difficulty),
    trailIds: r.trailIds,
    description: r.description ?? null,
    gpxFilename: r.gpxFilename,
    track: r.track,
    // Wave 14bk: 2m Deadband gegen GPS-ele-Noise-Akkumulation (s. trails.ts).
    stats: {
      lengthMeters: trackLength(r.track.points),
      ...elevationStats(r.track.points, { deadbandMeters: 2 }),
    },
    cachedAt: r.cachedAt,
    shuttleAreaCode: r.shuttleAreaCode ?? null,
  }));
}

/**
 * Liest alle Runs aus dem IDB-Cache — OHNE Backend-Fetch. Für Komponenten
 * die auf RUNS_UPDATED_EVENT reagieren und sich nur neu rendern wollen,
 * ohne erneut vom Backend zu ziehen (was sonst wieder den Event feuern würde).
 */
export async function listCachedRuns(): Promise<CachedRun[]> {
  const db = await getDb();
  const all = await db.getAll('runs');
  return all.map((r) => ({
    id: r.id,
    name: r.name,
    difficulty: normalizeDifficulty(r.difficulty),
    trailIds: r.trailIds,
    description: r.description ?? null,
    gpxFilename: r.gpxFilename,
    track: r.track,
    stats: {
      lengthMeters: trackLength(r.track.points),
      ...elevationStats(r.track.points, { deadbandMeters: 2 }),
    },
    cachedAt: r.cachedAt,
    shuttleAreaCode: r.shuttleAreaCode ?? null,
  }));
}

/** Einzel-Run aus dem IDB-Cache (ohne Netz-Fetch). Für RunDetailPage. */
export async function getCachedRun(id: string): Promise<CachedRun | null> {
  const db = await getDb();
  const r = await db.get('runs', id);
  if (!r) return null;
  return {
    id: r.id,
    name: r.name,
    difficulty: normalizeDifficulty(r.difficulty),
    trailIds: r.trailIds,
    description: r.description ?? null,
    gpxFilename: r.gpxFilename,
    track: r.track,
    // Wave 14bk: 2m Deadband gegen GPS-ele-Noise-Akkumulation (s. trails.ts).
    stats: {
      lengthMeters: trackLength(r.track.points),
      ...elevationStats(r.track.points, { deadbandMeters: 2 }),
    },
    cachedAt: r.cachedAt,
    shuttleAreaCode: r.shuttleAreaCode ?? null,
  };
}
